import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';

import { showModal } from '@/redux/reducers/modalsSlice';
import { addMixpanelEvent } from '@/redux/reducers/sharedSlice';
import { setPaginationPageSize } from '@/redux/reducers/spreadsheetSlice';

import { paginationArrowsSVG } from '@/Assets/icons';
import Icon from '@/Components/UI/Icon/Icon';
import { rowsAmountOptions } from '@/helpers/paginationHelper';
import customStyles from './style/selectStyles';

import { datasetSliceName, spreadsheetSliceName, userDataSliceName } from '@/redux/constants';
import { MODAL_CREATE_ACCOUNT, MODAL_GENERAL_UPGRADE, ROW_COUNT_FAILED } from '@/Utils/constants';

import { Button } from '@/Components/UI/Button/Button';
import './style/index.scss';
import { useCurrentFile } from '@/hooks/useCurrentFile';
import Tooltip from '@/Components/UI/Tooltip/Tooltip';
import SpinnerSolid from '@/Components/UI/Spinner/SpinnerSolid';
import { useShowPreviewModeUpsellModal } from '@/hooks/useShowPreviewModeUpsellModal';

const CustomPagination = ({
  agGrid,
  goToPage,
  pageNumber = 0,
  totalNumber = 0,
  totalNumberOfPages = 0,
}) => {
  const [pageInputValue, setPageInputValue] = useState(1);

  const paginationPageSize = useSelector((state) => state[spreadsheetSliceName].paginationPageSize);
  const totalGroupCount = useSelector((state) => state[spreadsheetSliceName].totalGroupCount);
  const filteredNumberOfRows = useSelector(
    (state) => state[spreadsheetSliceName].filteredNumberOfRows
  );
  const loadingRowCount = useSelector((state) => state[spreadsheetSliceName].loadingRowCount);
  const { isPreviewMode } = useSelector((state) => state[datasetSliceName]);

  const totalColumnCount = agGrid?.api.getColumnState()?.length || 0;
  const visibleColumnCount =
    agGrid?.api.getColumnState()?.filter((column) => !column.hide)?.length || 0;

  const currentFile = useCurrentFile();
  const dispatch = useDispatch();
  const showPreviewModeUpsellModal = useShowPreviewModeUpsellModal();
  const { user } = useSelector((store) => store[userDataSliceName]);
  const {
    userProperties: { maxPageSize },
    email,
  } = user || {};

  // Helper to decide whether to use totalNumber or fallback to currentFile?.metadata?.FileRows
  const getTotalCountDisplay = () => {
    if (totalNumber > 0) {
      return totalNumber.toLocaleString();
    }
    // fallback if totalNumber is 0 or falsy
    const fallback = currentFile?.metadata?.FileRows;
    return fallback != null ? fallback.toLocaleString() : '0';
  };

  const formatOptionLabel = ({ value }) => {
    const isAvailableOption = maxPageSize >= value;
    return (
      <div
        className={`${
          !isAvailableOption && 'text-gray-400'
        } relative flex flex-row w-full justify-between`}
      >
        <div>{value}</div>
        {!isAvailableOption && <Icon name='rocket-launch' color='#D9B500' />}
      </div>
    );
  };

  const onSelectVisibleRowsAmount = (option) => {
    if (isPreviewMode) {
      return showPreviewModeUpsellModal('View More Rows on the Premium Plan');
    } else if (maxPageSize < option.value && user?.is_anonymous) {
      dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
      return dispatch(
        addMixpanelEvent({
          eventName: `Paginate rows paywall (${option.value} rows)`,
          eventProps: { current_filename: currentFile?.metadata.FileName },
          userIncrementName: '# of clicks',
        })
      );
    } else if (maxPageSize < option.value) {
      dispatch(
        showModal({ name: MODAL_GENERAL_UPGRADE, props: { upgradeType: 'paginationView' } })
      );
      dispatch(
        addMixpanelEvent({
          eventName: `Paginate rows paywall (${option.value} rows)`,
          eventProps: { current_filename: currentFile?.metadata.FileName },
          userIncrementName: '# of clicks',
        })
      );
    } else {
      dispatch(setPaginationPageSize(option.value));
    }
  };

  function resizeInput() {
    this.style.width = this.value.length + 3.5 + 'ch';
  }

  const changePage = (_pageNumber) => {
    goToPage(_pageNumber);
    setPageInputValue(_pageNumber + 1);
  };

  const getInputSize = () => {
    return (pageInputValue + '').length + 3.5 + 'ch';
  };

  const rowCountCalculationFailed = () => {
    return filteredNumberOfRows === ROW_COUNT_FAILED || totalNumber === ROW_COUNT_FAILED;
  };

  useEffect(() => {
    const input = document.querySelector('#currentPage');

    if (input) {
      input.addEventListener('input', resizeInput);
      resizeInput.call(input);
    }

    return () => input?.removeEventListener('input', resizeInput);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (pageInputValue === '') return;

      goToPage(Number(pageInputValue - 1));
    }, 300);
    return () => {
      clearTimeout(timeout);
    };
  }, [pageInputValue, goToPage]);

  useEffect(() => {
    setPageInputValue(Number(pageNumber + 1));
  }, [pageNumber]);

  return (
    <div className='flex items-center'>
      <div className='flex items-center gap-4 ml-2 cursor-default pagination'>
        <div className='flex items-center justify-center'>
          <span className='text-sm font-semibold text-ui-secondary'>View</span>
          <Select
            id='rows-amount-select-container'
            inputId='rows-amount-select-input'
            isSearchable={false}
            styles={customStyles(maxPageSize !== '1000')}
            value={{ label: paginationPageSize, value: paginationPageSize }}
            options={rowsAmountOptions}
            onChange={onSelectVisibleRowsAmount}
            menuPlacement='top'
            closeMenuOnSelect={true}
            formatOptionLabel={formatOptionLabel}
            components={{
              IndicatorSeparator: () => null,
            }}
          />
        </div>
        <div className='flex pl-0 list-none rounded'>
          <Button
            disabled={pageNumber === 0}
            data-cy='first-page'
            className='firstPage'
            onClick={() => changePage(0)}
            color='shadow'
            size='small'
          >
            <img src={paginationArrowsSVG.StepToTheEnd} alt='first-page' />
          </Button>
          <Button
            disabled={pageNumber === 0}
            data-cy='previous-page'
            className='prevPage'
            onClick={() => changePage(pageNumber - 1)}
            color='shadow'
            size='small'
          >
            <img src={paginationArrowsSVG.NextStep} alt='previous-page' />
          </Button>

          <p className='!text-sm'>
            Page
            <input
              id='currentPage'
              data-cy='current-page'
              type='text'
              value={pageInputValue}
              style={{ width: getInputSize() || 0 }}
              onBlur={() => setPageInputValue(pageNumber + 1)}
              onChange={(event) => {
                const { value } = event.target;
                if (value === '') {
                  return setPageInputValue('');
                }
                if (!/^\d+$/.test(value)) return;
                setPageInputValue(value);
              }}
            />
            of&nbsp; <span id='totalPages'>{totalNumberOfPages}</span>
          </p>
          <Button
            data-cy='next-page'
            disabled={pageNumber === totalNumberOfPages - 1}
            className='rounded nextPage bg-shadow-200'
            onClick={() => changePage(pageNumber + 1)}
            color='shadow'
            size='small'
          >
            <img src={paginationArrowsSVG.NextStep} alt='previous-page' />
          </Button>

          <Button
            data-cy='last-page'
            disabled={pageNumber === totalNumberOfPages - 1}
            type='button'
            className='rounded lastPage bg-shadow-200'
            onClick={() => changePage(totalNumberOfPages - 1)}
            color='shadow'
            size='small'
          >
            <img src={paginationArrowsSVG.StepToTheEnd} alt='last-page' />
          </Button>
        </div>
        <div className='flex text-sm text-ui-secondary table-rows-counter'>
          <div className='flex mr-4'>
            <span className='mr-1 text-sm font-semibold text-ui-secondary'>Rows:</span>
            {loadingRowCount ? (
              <span className='flex items-center'>
                <SpinnerSolid size='small' />
                <span className='ml-1' id='totalRowCount'>
                  of {getTotalCountDisplay()}
                </span>
              </span>
            ) : rowCountCalculationFailed() ? (
              <span className='flex items-center'>
                <Tooltip text='Failed to calculate row count' side='top'>
                  <Icon name='smiley-sad' size={16} />
                </Tooltip>
                <span className='ml-1' id='totalRowCount'>
                  of {getTotalCountDisplay()}
                </span>
              </span>
            ) : (
              <span>
                {filteredNumberOfRows < (totalNumber || 0) && (
                  <span id='filteredRowCount'>{filteredNumberOfRows?.toLocaleString()} of </span>
                )}
                <span id='totalRowCount'>{getTotalCountDisplay()}</span>
              </span>
            )}
          </div>

          {totalGroupCount && (
            <div className='flex mr-4'>
              <span className='mr-1 text-sm font-semibold text-ui-secondary'>Groups:</span>{' '}
              <span>{totalGroupCount.toLocaleString()}</span>
            </div>
          )}

          <div className='mr-4'>
            <span className='text-sm font-semibold text-ui-secondary'>Cols:</span>{' '}
            <span>
              {visibleColumnCount < totalColumnCount
                ? `${visibleColumnCount.toLocaleString()} of ${totalColumnCount.toLocaleString()}`
                : `${totalColumnCount.toLocaleString()}`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomPagination;
